import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Identifier, useDataProvider } from 'react-admin';
import { MetadataAvatarTypeProperty } from '../utils/types';
import { storeMetadataBatch, setMetadataLoading } from '../redux/actions/metadata';
import { CustomReducerState } from '../redux/reducers';

// Global batch queue to collect requests across components
interface BatchRequest {
    metadataAvatarType: Identifier;
    names: Set<string>;
    subscribers: Map<string, () => void>;
    timeoutId?: NodeJS.Timeout;
}

const batchQueue = new Map<string, BatchRequest>();
const BATCH_DELAY = 50; // 50ms debounce time

export const useMetadataBatch = (
    metadataAvatarType: Identifier,
    propertyName: string
): MetadataAvatarTypeProperty | null => {
    console.log('🔧 useMetadataBatch called with:', { metadataAvatarType, propertyName });
    const dispatch = useDispatch();
    const dataProvider = useDataProvider();
    const [hasRequested, setHasRequested] = useState(false);
    const subscriberRef = useRef<(() => void) | null>(null);
    
    // Get cached data from Redux
    const cachedData = useSelector((state: CustomReducerState) => {
        const typeKey = String(metadataAvatarType);
        return state.rikutec?.metadata?.cache?.[typeKey]?.[propertyName] || null;
    });
    
    const isLoading = useSelector((state: CustomReducerState) => {
        const typeKey = String(metadataAvatarType);
        return state.rikutec?.metadata?.loading?.[typeKey] || false;
    });

    // Create subscriber callback
    const createSubscriber = useCallback(() => {
        return () => {
            // This will be called when batch data is ready
            setHasRequested(true);
        };
    }, []);

    // Register for batch request
    useEffect(() => {
        if (!metadataAvatarType || !propertyName) return;
        
        const typeKey = String(metadataAvatarType);
        
        // If we already have cached data, don't request again
        if (cachedData) {
            return;
        }
        
        // If already requested in this session, don't request again
        if (hasRequested) {
            return;
        }

        // Get or create batch request
        let batchRequest = batchQueue.get(typeKey);
        if (!batchRequest) {
            batchRequest = {
                metadataAvatarType,
                names: new Set(),
                subscribers: new Map()
            };
            batchQueue.set(typeKey, batchRequest);
        }

        // Add this property to the batch
        batchRequest.names.add(propertyName);
        
        // Create and store subscriber
        const subscriber = createSubscriber();
        subscriberRef.current = subscriber;
        batchRequest.subscribers.set(propertyName, subscriber);

        // Clear existing timeout
        if (batchRequest.timeoutId) {
            clearTimeout(batchRequest.timeoutId);
        }

                // Set new timeout to execute batch
                batchRequest.timeoutId = setTimeout(async () => {
                    try {
                        const currentBatchRequest = batchQueue.get(typeKey);
                        if (!currentBatchRequest) return;
                        
                        console.log('🚀 Executing metadata batch request for:', typeKey, 'with names:', Array.from(currentBatchRequest.names));
                        // Mark as loading
                        dispatch(setMetadataLoading.fn(metadataAvatarType, true));

                        // Prepare batch request parameters
                        const namesArray = Array.from(currentBatchRequest.names);
                        const filter: any = {
                            metadataAvatarType: metadataAvatarType
                        };
                        
                        // Add names as array parameter for batching
                        if (namesArray.length > 0) {
                            filter['name[]'] = namesArray;
                        }

                        // Make batch request
                        console.log('📡 Making batch request with filter:', filter);
                        const response = await dataProvider.getList('metadata_avatar_type_properties', {
                            pagination: { page: 1, perPage: 100 },
                            sort: { field: 'id', order: 'asc' },
                            filter: filter
                        });
                        console.log('✅ Batch response received:', response);

                        // Store results in Redux
                        if (response?.data && Array.isArray(response.data)) {
                            dispatch(storeMetadataBatch.fn(metadataAvatarType, response.data as MetadataAvatarTypeProperty[]));
                        }

                        // Notify all subscribers
                        currentBatchRequest.subscribers.forEach(subscriber => {
                            try {
                                subscriber();
                            } catch (error) {
                                console.warn('Error notifying metadata batch subscriber:', error);
                            }
                        });

                    } catch (error) {
                        console.error('Error fetching metadata batch:', error);
                        dispatch(setMetadataLoading.fn(metadataAvatarType, false));
                    } finally {
                        // Clean up
                        batchQueue.delete(typeKey);
                        setHasRequested(true);
                    }
        }, BATCH_DELAY);

        // Cleanup function
        return () => {
            if (batchRequest?.timeoutId) {
                clearTimeout(batchRequest.timeoutId);
            }
            if (subscriberRef.current && batchRequest?.subscribers) {
                batchRequest.subscribers.delete(propertyName);
            }
        };
    }, [metadataAvatarType, propertyName, cachedData, hasRequested, dispatch, dataProvider, createSubscriber]);

    // Return cached data if available, or null if still loading
    if (cachedData) {
        return cachedData;
    }
    
    // If we're loading or have requested, return null to show loading state
    if (isLoading || hasRequested) {
        return null;
    }
    
    return null;
};
